import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Send } from 'lucide-react';
import type { CommentEntityType } from '@forest/contracts';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import {
  useComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
} from './useComments';

type Props = {
  entityType: CommentEntityType;
  entityId: string;
};

export function CommentsPanel({ entityType, entityId }: Props) {
  const { t } = useTranslation(['comments']);
  const user = useAuthStore((s) => s.user);
  const pushToast = useUiStore((s) => s.pushToast);
  const query = useComments(entityType, entityId);
  const createMut = useCreateComment();
  const updateMut = useUpdateComment();
  const deleteMut = useDeleteComment();

  const [newBody, setNewBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');

  const comments = query.data ?? [];
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const handleCreate = async () => {
    if (!newBody.trim()) return;
    try {
      await createMut.mutateAsync({ entityType, entityId, body: newBody.trim() });
      setNewBody('');
      pushToast({ variant: 'success', message: t('comments:createSuccess') });
    } catch (e) {
      const err = await apiError(e);
      pushToast({
        variant: 'error',
        message: t('comments:createFailed', { message: err.message }),
      });
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editBody.trim()) return;
    try {
      await updateMut.mutateAsync({ id, input: { body: editBody.trim() }, entityType, entityId });
      setEditingId(null);
      setEditBody('');
      pushToast({ variant: 'success', message: t('comments:editSuccess') });
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('comments:deleteConfirm'))) return;
    try {
      await deleteMut.mutateAsync({ id, entityType, entityId });
      pushToast({ variant: 'success', message: t('comments:deleteSuccess') });
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const startEdit = (id: string, body: string) => {
    setEditingId(id);
    setEditBody(body);
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-700">{t('comments:title')}</h3>

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
          <Spinner />
        </div>
      ) : comments.length === 0 ? (
        <p className="py-3 text-sm text-slate-400">{t('comments:empty')}</p>
      ) : (
        <div className="max-h-60 space-y-2 overflow-auto">
          {comments.map((c) => (
            <div
              key={c.id}
              className="rounded border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-forest-100 text-xs font-semibold text-forest-700">
                    {c.userName.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-xs font-medium text-slate-700">{c.userName}</span>
                  <span className="text-xs text-slate-400">
                    {new Date(c.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="flex gap-1">
                  {user && c.userId === user.id ? (
                    <button
                      type="button"
                      onClick={() => startEdit(c.id, c.body)}
                      className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                      title={t('comments:edit')}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  ) : null}
                  {isSuperAdmin ? (
                    <button
                      type="button"
                      onClick={() => void handleDelete(c.id)}
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title={t('comments:delete')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
              </div>

              {editingId === c.id ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500"
                    rows={2}
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => void handleUpdate(c.id)}
                      loading={updateMut.isPending}
                    >
                      {t('comments:save')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditingId(null)}
                    >
                      {t('comments:cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{c.body}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New comment */}
      <div className="flex gap-2">
        <textarea
          className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500"
          rows={2}
          placeholder={t('comments:placeholder')}
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              void handleCreate();
            }
          }}
        />
        <Button
          onClick={() => void handleCreate()}
          loading={createMut.isPending}
          disabled={!newBody.trim()}
          className="self-end"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
